import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { publicApi } from "../lib/api";
import type { PublicShareDeal, PublicShareInfo, PublicShareMessage } from "@docreview/shared";
import Markdown from "../components/Markdown";

const SESSION_KEY_PREFIX = "share_session_";

function loadSession(token: string): { session: string; expiresAt: number } | null {
  try {
    const raw = localStorage.getItem(SESSION_KEY_PREFIX + token);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { session: string; expiresAt: number };
    if (parsed.expiresAt < Date.now()) {
      localStorage.removeItem(SESSION_KEY_PREFIX + token);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function saveSession(token: string, session: string, expiresAt: number): void {
  localStorage.setItem(
    SESSION_KEY_PREFIX + token,
    JSON.stringify({ session, expiresAt }),
  );
}

function clearSession(token: string): void {
  localStorage.removeItem(SESSION_KEY_PREFIX + token);
}

export default function SharePage() {
  const { token } = useParams<{ token: string }>();
  const shareToken = token!;
  const [info, setInfo] = useState<PublicShareInfo | { status: "expired" | "revoked" } | null | "loading">("loading");
  const [session, setSession] = useState<string | null>(null);

  useEffect(() => {
    const stored = loadSession(shareToken);
    if (stored) setSession(stored.session);
    publicApi.getInfo(shareToken).then((i) => setInfo(i));
  }, [shareToken]);

  if (info === "loading") {
    return <FullScreen>Loading…</FullScreen>;
  }
  if (!info) {
    return <FullScreen>This share link doesn't exist.</FullScreen>;
  }
  if (info.status !== "active") {
    return (
      <FullScreen>
        <div className="text-center">
          <div className="text-lg font-semibold mb-2">Share unavailable</div>
          <div className="text-sm text-slate-500">
            {info.status === "expired"
              ? "This share link has expired."
              : "This share link has been revoked."}
          </div>
        </div>
      </FullScreen>
    );
  }

  if (!session) {
    return (
      <PasswordGate
        token={shareToken}
        info={info}
        onAuthed={(s, expiresAt) => {
          saveSession(shareToken, s, expiresAt);
          setSession(s);
        }}
      />
    );
  }

  return (
    <SharedDealView
      token={shareToken}
      info={info}
      session={session}
      onSignOut={() => {
        clearSession(shareToken);
        setSession(null);
      }}
    />
  );
}

function FullScreen({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-bg p-4 text-slate-700">
      <div className="card p-8 max-w-md w-full">{children}</div>
    </div>
  );
}

function PasswordGate({
  token,
  info,
  onAuthed,
}: {
  token: string;
  info: PublicShareInfo;
  onAuthed: (session: string, expiresAt: number) => void;
}) {
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e?: React.FormEvent) {
    e?.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      const { session, expiresAt } = await publicApi.authenticate(token, password);
      onAuthed(session, expiresAt);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setErr(
        msg === "wrong_password"
          ? "Incorrect password."
          : msg === "share_unavailable"
            ? "This share has expired or been revoked."
            : msg,
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <FullScreen>
      <form onSubmit={submit}>
        <div className="text-xs uppercase tracking-wide text-slate-400 mb-1">
          Document Review
        </div>
        <h1 className="font-semibold text-lg">{info.dealTitle}</h1>
        <div className="text-sm text-slate-700">{info.reviewTitle}</div>
        <div className="text-sm text-slate-500 mb-4">
          {info.dealType} · {info.reviewStage.replace("_", " ")}
        </div>
        <p className="text-sm text-slate-600 mb-4">
          This is a read-only view shared by Chicago Venture Partners. Enter the password you were
          given to access it.
        </p>
        <input
          type="password"
          className="input mb-3 font-mono"
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoFocus
        />
        {err && <div className="text-sm text-red-600 mb-3">{err}</div>}
        <button
          className="btn-primary w-full justify-center"
          disabled={busy || !password}
          type="submit"
        >
          {busy ? "Verifying…" : "Continue"}
        </button>
        <div className="text-xs text-slate-400 mt-4 text-center">
          Expires {new Date(info.expiresAt).toLocaleString()}
        </div>
      </form>
    </FullScreen>
  );
}

function SharedDealView({
  token,
  info,
  session,
  onSignOut,
}: {
  token: string;
  info: PublicShareInfo;
  session: string;
  onSignOut: () => void;
}) {
  const [deal, setDeal] = useState<PublicShareDeal | null>(null);
  const [messages, setMessages] = useState<PublicShareMessage[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    Promise.all([publicApi.getDeal(token, session), publicApi.getMessages(token, session)])
      .then(([d, m]) => {
        setDeal(d);
        setMessages(m);
      })
      .catch((e) => {
        const msg = e instanceof Error ? e.message : String(e);
        if (/410|401|invalid|unavailable/i.test(msg)) {
          onSignOut();
        } else {
          setErr(msg);
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, session]);

  if (err) {
    return <FullScreen>Error: {err}</FullScreen>;
  }
  if (!deal) {
    return <FullScreen>Loading…</FullScreen>;
  }

  return (
    <div className="min-h-screen flex flex-col bg-bg">
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-wide text-slate-400">
              Document Review · shared link
            </div>
            <div className="font-semibold">{deal.title}</div>
            <div className="text-xs text-slate-500">{deal.reviewTitle}</div>
          </div>
          <div className="text-xs text-slate-500 text-right">
            <div>read-only</div>
            <div>expires {new Date(info.expiresAt).toLocaleString()}</div>
          </div>
        </div>
      </header>
      <main className="flex-1 max-w-5xl mx-auto w-full px-4 py-6 grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6">
        <aside className="space-y-3">
          <div className="card p-4">
            <div className="text-xs uppercase tracking-wide text-slate-400 mb-1">Deal</div>
            <div className="font-medium">{deal.title}</div>
            <div className="text-xs text-slate-500 mt-1">
              {deal.dealType} · {deal.reviewStage.replace("_", " ")}
            </div>
            <div className="text-xs text-slate-500 mt-1">Review: {deal.reviewTitle}</div>
            {deal.notes && deal.notes.trim().length > 0 && (
              <div className="mt-3 pt-3 border-t border-slate-100">
                <div className="text-xs uppercase tracking-wide text-slate-400 mb-1">
                  Notes
                </div>
                <div className="text-sm text-slate-700 whitespace-pre-wrap">
                  {deal.notes}
                </div>
              </div>
            )}
          </div>
          <button
            className="btn-secondary w-full justify-center text-xs"
            onClick={onSignOut}
          >
            Sign out
          </button>
        </aside>
        <section ref={scrollRef} className="space-y-4 min-w-0">
          {messages.length === 0 ? (
            <div className="card p-4 text-sm text-slate-500">
              No messages have been written for this deal yet.
            </div>
          ) : (
            messages.map((m) => <SharedMessageView key={m.id} m={m} />)
          )}
        </section>
      </main>
    </div>
  );
}

function SharedMessageView({ m }: { m: PublicShareMessage }) {
  const isUser = m.role === "user";
  return (
    <div className={`card p-4 ${isUser ? "bg-slate-50" : ""}`}>
      <div className="text-xs text-slate-500 mb-1">{m.authorName}</div>
      {isUser ? (
        <div className="whitespace-pre-wrap text-sm">{m.content}</div>
      ) : (
        <Markdown>{m.content}</Markdown>
      )}
    </div>
  );
}
